import {
  POSITIVE_COLOR,
  PRIMARY_COLOR,
  plotColors,
  type SankeyData,
  type SankeyLink,
  type SankeyNode,
} from '@revelio/replots';

import type {
  SingleDataPoint,
  TransitionBreakdownData,
} from '../../DataProvider/types';
import { THREE_TIER_NODE_SUFFIXES } from './sankeyNodeIds';

function getTopBaseIdsByValue(
  breakdowns: Array<SingleDataPoint[] | undefined>,
  limit: number
): Set<string> {
  const totals = new Map<string, number>();

  breakdowns.forEach((breakdown) => {
    (breakdown ?? []).forEach(({ dim, id, value }) => {
      const baseId = id ?? dim;
      if (!baseId) return;
      totals.set(baseId, (totals.get(baseId) ?? 0) + (value ?? 0));
    });
  });

  const ranked = [...totals.entries()]
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([baseId]) => baseId);

  return new Set(ranked);
}

function filterBreakdownByBaseIds(
  breakdown: SingleDataPoint[] | undefined,
  topBaseIds: Set<string>
): SingleDataPoint[] {
  return (breakdown ?? []).filter(({ dim, id }) => {
    const baseId = id ?? dim;
    return baseId && topBaseIds.has(baseId);
  });
}

type ExternalSummary = {
  connectedPrimaryIds: Set<string>;
  totalValue: number;
};

type BuildThreeTierSankeyDataParams = {
  data: TransitionBreakdownData[];
  effectiveNumOfNodes: number;
  color?: string;
};

export function buildThreeTierSankeyData({
  data,
  effectiveNumOfNodes,
  color,
}: BuildThreeTierSankeyDataParams): SankeyData | null {
  const primaryEntries = data ?? [];
  if (primaryEntries.length === 0) return null;

  const primaryColorAt = (idx: number): string => {
    if (idx === 0) return color ?? plotColors[0] ?? PRIMARY_COLOR;
    return plotColors[idx % plotColors.length] ?? POSITIVE_COLOR;
  };

  const primaryNodes: SankeyNode[] = [];
  const inflowNodes: SankeyNode[] = [];
  const outflowNodes: SankeyNode[] = [];
  const seenNodeIds = new Set<string>();
  const colorByPrimaryId = new Map<string, string>();
  const primaryIdSet = new Set<string>();

  primaryEntries.forEach((entry, idx) => {
    const label = entry.dim ?? '';
    const id = entry.id ?? label;
    if (!id || seenNodeIds.has(id)) return;
    primaryNodes.push({ id, label });
    seenNodeIds.add(id);
    primaryIdSet.add(id);
    colorByPrimaryId.set(id, primaryColorAt(idx));
  });

  const topInflowBaseIds = getTopBaseIdsByValue(
    primaryEntries.map((entry) => entry.inflowBreakdown),
    effectiveNumOfNodes
  );
  const topOutflowBaseIds = getTopBaseIdsByValue(
    primaryEntries.map((entry) => entry.outflowBreakdown),
    effectiveNumOfNodes
  );

  const inflowSummary = new Map<string, ExternalSummary>();
  const outflowSummary = new Map<string, ExternalSummary>();
  const links: SankeyLink[] = [];

  const resolveExternalNode = ({
    baseId,
    dim,
    tierSuffix,
    cycleSuffix,
    nodeList,
  }: {
    baseId: string;
    dim: string;
    tierSuffix: string;
    cycleSuffix: string;
    nodeList: SankeyNode[];
  }): string => {
    const isCycle = primaryIdSet.has(baseId);
    const safeId = isCycle
      ? `${baseId}${cycleSuffix}`
      : `${baseId}${tierSuffix}`;

    if (!seenNodeIds.has(safeId)) {
      nodeList.push({ id: safeId, label: dim });
      seenNodeIds.add(safeId);
    }
    return safeId;
  };

  primaryEntries.forEach((entry) => {
    const primaryId = entry.id ?? entry.dim ?? '';
    if (!primaryId) return;

    const primaryColor = colorByPrimaryId.get(primaryId) ?? primaryColorAt(0);

    const inflowSlice = filterBreakdownByBaseIds(
      entry.inflowBreakdown,
      topInflowBaseIds
    );
    inflowSlice.forEach(({ dim, id, value, share }) => {
      const baseId = id ?? dim;
      const safeId = resolveExternalNode({
        baseId,
        dim,
        tierSuffix: THREE_TIER_NODE_SUFFIXES.inflow,
        cycleSuffix: THREE_TIER_NODE_SUFFIXES.cycleInflow,
        nodeList: inflowNodes,
      });

      const linkValue = value ?? 0;

      const summary = inflowSummary.get(safeId) ?? {
        connectedPrimaryIds: new Set<string>(),
        totalValue: 0,
      };
      summary.connectedPrimaryIds.add(primaryId);
      summary.totalValue += linkValue;
      inflowSummary.set(safeId, summary);

      links.push({
        source: safeId,
        target: primaryId,
        value: linkValue,
        share: share ?? 0,
        color: primaryColor,
        // Pair this entity's inflow and outflow ribbons for hover highlighting.
        group: `${baseId}::${primaryId}`,
      });
    });

    const outflowSlice = filterBreakdownByBaseIds(
      entry.outflowBreakdown,
      topOutflowBaseIds
    );
    outflowSlice.forEach(({ dim, id, value, share }) => {
      const baseId = id ?? dim;
      const safeId = resolveExternalNode({
        baseId,
        dim,
        tierSuffix: THREE_TIER_NODE_SUFFIXES.outflow,
        cycleSuffix: THREE_TIER_NODE_SUFFIXES.cycleOutflow,
        nodeList: outflowNodes,
      });

      const linkValue = value ?? 0;

      const summary = outflowSummary.get(safeId) ?? {
        connectedPrimaryIds: new Set<string>(),
        totalValue: 0,
      };
      summary.connectedPrimaryIds.add(primaryId);
      summary.totalValue += linkValue;
      outflowSummary.set(safeId, summary);

      links.push({
        source: primaryId,
        target: safeId,
        value: linkValue,
        share: share ?? 0,
        color: primaryColor,
        group: `${baseId}::${primaryId}`,
      });
    });
  });

  if (links.length === 0) return null;

  // Order each tier by total flow, highest first.
  const orderExternals = (
    externalNodes: SankeyNode[],
    summaryMap: Map<string, ExternalSummary>
  ): SankeyNode[] =>
    [...externalNodes].sort(
      (a, b) =>
        (summaryMap.get(b.id)?.totalValue ?? 0) -
        (summaryMap.get(a.id)?.totalValue ?? 0)
    );

  const orderedInflowNodes = orderExternals(inflowNodes, inflowSummary);
  const orderedOutflowNodes = orderExternals(outflowNodes, outflowSummary);

  return {
    nodes: [...orderedInflowNodes, ...primaryNodes, ...orderedOutflowNodes],
    links,
  };
}
